import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { fetchCachedAuthUser, writeCachedAuthUser } from "../utils/authCache";
import { getAuthToken } from "../utils/auth";
import { useNavigate } from "react-router-dom";
const API_BASE = String(
  import.meta.env.VITE_API_BASE || import.meta.env.VITE_API_BASE_URL || "https://api.avaaio.com"
).replace(/\/+$/, "");
function getProfileAuthHeaders(includeJson = true) {
  const headers = {};
  if (includeJson) {
    headers["Content-Type"] = "application/json";
  }
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}
function getCookie(name) {
  if (typeof document === "undefined") return null;
  const safe = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${safe}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
function getStoredProfile() {
  const keys = [];
  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key) || getCookie(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      continue;
    }
  }
  return null;
}
function pretty(value, fallback = "Not set") {
  if (value === null || value === void 0) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
}
function Profile() {
  const navigate = useNavigate();
  const [apiUser, setApiUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const storedUser = useMemo(() => getStoredProfile(), []);
  const [cloudAiAllowed, setCloudAiAllowed] = useState(null);
  const [cloudAiBusy, setCloudAiBusy] = useState(false);
  const [cloudAiManageOpen, setCloudAiManageOpen] = useState(false);
  const [cloudAiNotice, setCloudAiNotice] = useState("");
  async function loadCloudAiConsent() {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/privacy/ai-consent`,
        {
          method: "GET",
          credentials: "include",
          headers: getProfileAuthHeaders(false)
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.detail || data?.error || `Cloud AI consent read failed: ${response.status}`
        );
      }
      setCloudAiAllowed(
        data?.allowed === true
      );
      setCloudAiNotice("");
    } catch (error) {
      console.warn(
        "[Profile] Cloud AI consent read failed:",
        error
      );
      setCloudAiAllowed(null);
      setCloudAiNotice(
        "Cloud AI Processing status could not be loaded."
      );
    }
  }
  async function saveCloudAiConsent(allowed) {
    if (cloudAiBusy) {
      return;
    }
    setCloudAiBusy(true);
    setCloudAiNotice("");
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/privacy/ai-consent`,
        {
          method: "POST",
          credentials: "include",
          headers: getProfileAuthHeaders(),
          body: JSON.stringify({
            allowed
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.detail || data?.error || `Cloud AI consent update failed: ${response.status}`
        );
      }
      const nextAllowed = typeof data?.allowed === "boolean" ? data.allowed : allowed;
      setCloudAiAllowed(nextAllowed);
      setCloudAiNotice(
        nextAllowed ? "Cloud AI Processing enabled." : "Cloud AI Processing disabled."
      );
    } catch (error) {
      console.warn(
        "[Profile] Cloud AI consent update failed:",
        error
      );
      setCloudAiNotice(
        "Cloud AI Processing could not be updated. Please try again."
      );
    } finally {
      setCloudAiBusy(false);
    }
  }
  useEffect(() => {
    let cancelled = false;
    fetchCachedAuthUser(`${API_BASE}/api/v1/auth/me`, {
      credentials: "include"
    }).then((authUser) => {
      if (cancelled || !authUser) return;
      if (authUser && typeof authUser === "object") {
        setApiUser(authUser);
      }
    }).catch((err) => {
      console.warn("[Profile] auth/me read failed:", err);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    void loadCloudAiConsent();
  }, []);
  const merged = {
    ...storedUser || {},
    ...apiUser || {},
    memory: {
      ...storedUser?.memory || {},
      ...apiUser?.memory || {}
    }
  };
  const localMemoryLearning = typeof window !== "undefined" ? localStorage.getItem("ava_memory_learning_enabled") : null;
  const memoryLearningEnabled = localMemoryLearning === "false" ? false : localMemoryLearning === "true" ? true : merged.memory?.learning_enabled === true;
  const displayName = pretty(
    merged.display_name || merged.name || "User"
  );
  const email = pretty(merged.email, "Unknown");
  const liveRole = String(apiUser?.role || merged.role || "user").toLowerCase();
  const liveSub = String(String(apiUser?.subscription_status || "inactive")).toLowerCase();
  const liveVip = Boolean(apiUser?.vip_lifetime ?? false);
  const role = pretty(liveRole || "user", "User");
  const isAdminIdentity = ["admin", "owner", "superadmin", "super_admin"].includes(liveRole);
  const isProIdentity = !isAdminIdentity && (liveRole === "vip" || liveSub === "active" || liveVip);
  const plan = isAdminIdentity ? "Admin" : isProIdentity ? "Pro" : "Free";
  const identityStatus = isAdminIdentity ? "\u2726 Admin Verified \u2022 Command Access Enabled" : isProIdentity ? "\u2726 Identity Verified \u2022 AVAAIO\u2122 Connected" : "\u2726 Identity Active \u2022 Free Access";
  const status = pretty(merged.status || merged.memory?.status);
  const city = pretty(merged.city || merged.memory?.city);
  const rawZip = pretty(merged.zip);
  const zip = rawZip === "12345" ? "Not set" : rawZip;
  const theme = pretty(
    merged.theme || merged.theme_color || merged.memory?.theme_color || localStorage.getItem("ava_theme") || localStorage.getItem("theme"),
    "Default"
  );
  const voice = pretty(
    merged.voice || merged.voiceName || merged.memory?.voice,
    "Default"
  );
  const provider = pretty(
    merged.provider || merged.auth_provider || merged.loginProvider || merged.memory?.provider,
    "Email"
  );
  const [editingProfile, setEditingProfile] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState(displayName === "Not set" ? "" : displayName);
  const [editZip, setEditZip] = useState(zip === "Not set" ? "" : zip);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveNotice, setProfileSaveNotice] = useState("");
  async function saveEditableProfile(e) {
    e?.preventDefault();
    e?.stopPropagation();
    const cleanName = editDisplayName.trim();
    const cleanZip = editZip.trim().replace(/[^0-9]/g, "").slice(0, 10);
    const finalZip = cleanZip === "12345" ? "" : cleanZip;
    if (!cleanName) {
      alert("Display name cannot be blank.");
      return;
    }
    setProfileSaving(true);
    setProfileSaveNotice("");
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/profile`, {
        method: "PATCH",
        headers: getProfileAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({
          display_name: cleanName,
          zip: finalZip
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.detail || data?.error || `Profile save failed: ${res.status}`);
      }
      const optimisticUser = {
        ...apiUser || {},
        display_name: cleanName,
        name: cleanName,
        zip: cleanZip,
        zip_code: cleanZip,
        memory: {
          ...apiUser?.memory || {},
          display_name: cleanName,
          zip: cleanZip
        }
      };
      try {
        writeCachedAuthUser(optimisticUser);
      } catch (cacheErr) {
        console.warn("[Profile] saved, but cache write skipped:", cacheErr);
      }
      setApiUser(optimisticUser);
      setEditingProfile(false);
      console.log("[Profile] Profile updated.");
    } catch (err) {
      console.warn("[Profile] profile save warning:", err);
    } finally {
      setProfileSaving(false);
    }
  }
  async function saveProfileMemoryPatch(memoryPatch) {
    const res = await fetch(`${API_BASE}/api/v1/auth/profile`, {
      method: "PATCH",
      headers: getProfileAuthHeaders(),
      credentials: "include",
      body: JSON.stringify({ memory: memoryPatch })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) {
      throw new Error(
        data?.detail || data?.error || `Profile memory save failed: ${res.status}`
      );
    }
    console.log("[Profile] memory patch saved:", data);
    return data;
  }
  const pageStyle = {
    minHeight: "100vh",
    background: "radial-gradient(circle at top, rgba(0,255,255,0.08) 0%, rgba(0,255,255,0.02) 24%, transparent 46%), linear-gradient(180deg, #02050b 0%, #040912 48%, #010203 100%)",
    color: "#e7f4ff",
    fontFamily: "Orbitron, sans-serif",
    padding: "20px 16px 28px",
    boxSizing: "border-box"
  };
  const shellStyle = {
    maxWidth: "1180px",
    margin: "0 auto",
    padding: "8px"
  };
  const heroStyle = {
    borderRadius: "28px",
    padding: "28px 24px 24px",
    marginBottom: "22px",
    background: "linear-gradient(135deg, rgba(0,229,255,0.10), rgba(255,255,255,0.025) 45%, rgba(0,0,0,0.18))",
    border: "1px solid rgba(125,249,255,0.10)",
    boxShadow: "0 22px 60px rgba(0,0,0,0.38), inset 0 0 28px rgba(0,229,255,0.035)",
    backdropFilter: "blur(14px)"
  };
  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px"
  };
  const cardStyle = {
    borderRadius: "28px",
    padding: "20px",
    minHeight: "86px",
    background: "linear-gradient(145deg, rgba(8,15,28,0.78), rgba(2,6,14,0.62))",
    border: "1px solid rgba(125,249,255,0.09)",
    boxShadow: "0 16px 42px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.025)",
    backdropFilter: "blur(12px)"
  };
  const labelStyle = {
    fontSize: "11px",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "#79f3ff",
    fontWeight: 800,
    marginBottom: "8px"
  };
  const valueStyle = {
    fontSize: "16px",
    lineHeight: 1.5,
    color: "#f4fdff",
    fontWeight: 600,
    wordBreak: "break-word"
  };
  const buttonRowStyle = {
    marginTop: "18px",
    display: "flex",
    gap: "12px",
    flexWrap: "wrap"
  };
  const inputStyle = {
    width: "100%",
    minHeight: "44px",
    borderRadius: "28px",
    border: "1px solid rgba(0,240,255,0.35)",
    background: "rgba(3, 10, 18, 0.88)",
    color: "#f4fdff",
    padding: "0 12px",
    fontWeight: 800,
    letterSpacing: "0.02em",
    outline: "none",
    boxSizing: "border-box"
  };
  const profileEditButtonStyle = {
    minHeight: "42px",
    borderRadius: "28px",
    padding: "0 14px",
    border: "1px solid rgba(0,240,255,0.45)",
    background: "rgba(6, 14, 24, 0.92)",
    color: "#e7f4ff",
    fontWeight: 800,
    fontSize: "13px",
    cursor: "pointer",
    boxShadow: "0 14px 40px rgba(0,0,0,0.32)",
    backdropFilter: "blur(12px)",
    letterSpacing: "0.03em"
  };
  const buttonStyle = {
    minHeight: "48px",
    borderRadius: "28px",
    padding: "0 18px",
    border: "1px solid rgba(0,240,255,0.45)",
    background: "rgba(6, 14, 24, 0.92)",
    color: "#e7f4ff",
    fontWeight: 800,
    fontSize: "14px",
    cursor: "pointer",
    boxShadow: "0 14px 40px rgba(0,0,0,0.32)",
    backdropFilter: "blur(12px)",
    letterSpacing: "0.03em"
  };
  return /* @__PURE__ */ jsx("div", { style: pageStyle, children: /* @__PURE__ */ jsxs("div", { style: shellStyle, children: [
    /* @__PURE__ */ jsxs("section", { style: heroStyle, children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            fontSize: "11px",
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "#79f3ff",
            fontWeight: 800,
            marginBottom: "8px"
          },
          children: "AVAAIO Identity"
        }
      ),
      /* @__PURE__ */ jsx(
        "h1",
        {
          style: {
            margin: 0,
            fontSize: "clamp(34px, 6vw, 68px)",
            lineHeight: 1,
            fontWeight: 900,
            color: "#19e6ff",
            textShadow: "0 0 28px rgba(0,255,255,0.28)",
            background: "linear-gradient(90deg,#8ffcff,#ffffff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "0.04em"
          },
          children: "PROFILE"
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "18px",
            padding: "10px 16px",
            borderRadius: "999px",
            border: "1px solid rgba(143,247,255,0.22)",
            background: "rgba(0,255,255,0.08)",
            color: "#dffcff",
            fontWeight: 800,
            fontSize: "13px",
            letterSpacing: "0.04em",
            boxShadow: "0 0 18px rgba(0,255,255,0.12)",
            width: "fit-content"
          },
          children: identityStatus
        }
      ),
      /* @__PURE__ */ jsx(
        "p",
        {
          style: {
            marginTop: "18px",
            marginBottom: 0,
            maxWidth: "860px",
            color: "rgba(236,250,255,0.78)",
            fontSize: "15px",
            lineHeight: 1.6
          },
          children: "Review and control your AVAAIO\u2122 identity, memory, personalization, subscription status, and Ava experience from one intelligent command center."
        }
      ),
      loading ? /* @__PURE__ */ jsx("div", { style: { marginTop: "14px", color: "rgba(236,250,255,0.72)" }, children: "Loading profile data\u2026" }) : null
    ] }),
    /* @__PURE__ */ jsxs("section", { style: gridStyle, children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            ...cardStyle,
            background: "linear-gradient(180deg, rgba(8,18,32,0.96), rgba(4,10,18,0.92))"
          },
          children: [
            /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Display Name" }),
            editingProfile ? /* @__PURE__ */ jsx(
              "input",
              {
                style: inputStyle,
                value: editDisplayName,
                onChange: (e) => setEditDisplayName(e.target.value),
                placeholder: "Enter display name"
              }
            ) : /* @__PURE__ */ jsx("div", { style: valueStyle, children: displayName })
          ]
        }
      ),
      /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
        /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Email" }),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              ...valueStyle,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%"
            },
            title: email,
            children: email
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
        /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Plan" }),
        /* @__PURE__ */ jsx("div", { style: valueStyle, children: plan })
      ] }),
      /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
        /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Status" }),
        /* @__PURE__ */ jsx("div", { style: valueStyle, children: status })
      ] }),
      isAdminIdentity ? /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
        /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Provider" }),
        /* @__PURE__ */ jsx("div", { style: valueStyle, children: provider })
      ] }) : null,
      /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
        /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Theme" }),
        /* @__PURE__ */ jsx("div", { style: valueStyle, children: theme })
      ] }),
      /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
        /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Voice" }),
        /* @__PURE__ */ jsx("div", { style: valueStyle, children: voice })
      ] }),
      /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
        /* @__PURE__ */ jsx("div", { style: labelStyle, children: "ZIP" }),
        editingProfile ? /* @__PURE__ */ jsx(
          "input",
          {
            style: inputStyle,
            value: editZip === "12345" ? "" : editZip,
            onChange: (e) => setEditZip(e.target.value),
            placeholder: "Enter ZIP code"
          }
        ) : /* @__PURE__ */ jsx("div", { style: valueStyle, children: zip })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { style: buttonRowStyle, children: !editingProfile ? /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        style: profileEditButtonStyle,
        onClick: () => {
          setEditDisplayName(displayName === "Not set" ? "" : displayName);
          setEditZip(zip === "Not set" || zip === "12345" ? "" : zip);
          setEditingProfile(true);
        },
        children: "Edit Profile"
      }
    ) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          style: profileEditButtonStyle,
          disabled: profileSaving,
          onClick: saveEditableProfile,
          children: profileSaving ? "Saving..." : "Save Profile"
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          style: profileEditButtonStyle,
          disabled: profileSaving,
          onClick: () => {
            setEditDisplayName(displayName === "Not set" ? "" : displayName);
            setEditZip(zip === "Not set" || zip === "12345" ? "" : zip);
            setEditingProfile(false);
          },
          children: "Cancel"
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx("section", { style: { marginTop: "28px" }, children: /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
      /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Cloud AI Processing" }),
      /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            ...valueStyle,
            fontSize: "14px",
            opacity: 0.85,
            lineHeight: 1.6
          },
          children: "Controls whether AVAAIO may send information to OpenAI, Microsoft Azure Speech, and Exa for cloud AI, semantic memory matching, voice, and live web research features."
        }
      ),
      /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            marginTop: "14px",
            ...valueStyle,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap"
          },
          children: [
            /* @__PURE__ */ jsx("span", { children: cloudAiAllowed === null ? "Checking\u2026" : cloudAiAllowed ? "Enabled" : "Disabled" }),
            /* @__PURE__ */ jsx(
              "span",
              {
                "aria-hidden": "true",
                style: {
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  display: "inline-block",
                  background: cloudAiAllowed === null ? "#ffc857" : cloudAiAllowed ? "#00c853" : "#ff3b30"
                }
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx("div", { style: buttonRowStyle, children: /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          style: buttonStyle,
          onClick: () => {
            setCloudAiManageOpen(
              (open) => !open
            );
          },
          children: "Manage AI Processing"
        }
      ) }),
      cloudAiManageOpen ? /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            marginTop: "16px",
            padding: "16px",
            borderRadius: "22px",
            border: "1px solid rgba(0,240,255,0.18)",
            background: "rgba(0,10,18,0.48)"
          },
          children: [
            /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  ...valueStyle,
                  fontSize: "13px",
                  lineHeight: 1.6
                },
                children: [
                  "Current setting:",
                  " ",
                  cloudAiAllowed === null ? "Checking\u2026" : cloudAiAllowed ? "Enabled" : "Disabled"
                ]
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  marginTop: "10px",
                  color: "rgba(236,250,255,0.72)",
                  fontSize: "12px",
                  lineHeight: 1.6
                },
                children: "When enabled, supported AVAAIO features may use OpenAI, Microsoft Azure Speech, and Exa when those services are needed. Memory Learning is controlled separately."
              }
            ),
            /* @__PURE__ */ jsxs("div", { style: buttonRowStyle, children: [
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  style: buttonStyle,
                  disabled: cloudAiBusy || cloudAiAllowed === null,
                  onClick: () => {
                    void saveCloudAiConsent(
                      !Boolean(cloudAiAllowed)
                    );
                  },
                  children: cloudAiBusy ? "Saving..." : cloudAiAllowed ? "Disable Cloud AI Processing" : "Enable Cloud AI Processing"
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  style: profileEditButtonStyle,
                  onClick: () => {
                    setCloudAiManageOpen(false);
                  },
                  children: "Close"
                }
              )
            ] })
          ]
        }
      ) : null,
      cloudAiNotice ? /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            marginTop: "12px",
            color: cloudAiNotice.includes(
              "could not"
            ) ? "#ff8a8a" : "#7cffb2",
            fontSize: "13px",
            fontWeight: 800
          },
          children: cloudAiNotice
        }
      ) : null
    ] }) }),
    /* @__PURE__ */ jsx("section", { style: { marginTop: "28px" }, children: /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
      /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Memory Learning" }),
      /* @__PURE__ */ jsxs("div", { style: { ...valueStyle, display: "flex", alignItems: "center", gap: "12px" }, children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": "Toggle memory learning",
            onClick: async () => {
              const newValue = !memoryLearningEnabled;
              try {
                localStorage.setItem("ava_memory_learning_enabled", String(newValue));
                sessionStorage.setItem("ava_memory_learning_enabled", String(newValue));
                const raw = localStorage.getItem("ava_user") || sessionStorage.getItem("ava_user");
                const saved = raw ? JSON.parse(raw) : {};
                const next = {
                  ...saved,
                  memory: {
                    ...saved.memory || {},
                    learning_enabled: newValue
                  }
                };
                localStorage.setItem("ava_user", JSON.stringify(next));
                sessionStorage.setItem("ava_user", JSON.stringify(next));
              } catch {
              }
              await saveProfileMemoryPatch({
                learning_enabled: newValue
              });
              window.location.reload();
            },
            style: {
              width: "52px",
              height: "28px",
              borderRadius: "28px",
              border: "1px solid rgba(255,255,255,0.25)",
              background: !memoryLearningEnabled ? "#ff3b30" : "#00c853",
              padding: "3px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: !memoryLearningEnabled ? "flex-start" : "flex-end",
              transition: "all 0.25s ease"
            },
            children: /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  width: "20px",
                  height: "20px",
                  borderRadius: "28px",
                  background: "#fff",
                  display: "block",
                  boxShadow: "0 0 8px rgba(0,0,0,0.35)"
                }
              }
            )
          }
        ),
        /* @__PURE__ */ jsx("span", { children: !memoryLearningEnabled ? "Disabled" : "Enabled" })
      ] })
    ] }) }),
    /* @__PURE__ */ jsxs("section", { style: { marginTop: "28px" }, children: [
      /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Ava Memory" }),
      /* @__PURE__ */ jsxs("div", { style: cardStyle, children: [
        /* @__PURE__ */ jsx("div", { style: { ...valueStyle, fontSize: "14px", opacity: 0.85 }, children: "Ava uses memory to personalize your experience \u2014 including your name, preferences, theme, and interaction style." }),
        /* @__PURE__ */ jsxs("div", { style: { marginTop: "12px", ...valueStyle }, children: [
          "Memory Status: ",
          merged.memory ? "Active \u2726 Personalized" : "Not Active"
        ] }),
        profileSaveNotice && /* @__PURE__ */ jsx("div", { style: { marginTop: "14px", marginBottom: "10px", color: profileSaveNotice.startsWith("\u2705") ? "#7CFFB2" : "#FF8A8A", fontWeight: 800 }, children: profileSaveNotice }),
        /* @__PURE__ */ jsx("div", { style: buttonRowStyle, children: /* @__PURE__ */ jsx(
          "button",
          {
            style: buttonStyle,
            onClick: async () => {
              const ok = window.confirm(
                "Clear Ava memory for this profile?\n\nOK = clear memory\nCancel = keep memory"
              );
              if (!ok) return;
              try {
                const deleteRes = await fetch(
                  `${API_BASE}/api/v1/auth/profile/memory`,
                  {
                    method: "DELETE",
                    credentials: "include",
                    headers: getProfileAuthHeaders(false)
                  }
                );
                const deleteData = await deleteRes.json().catch(() => ({}));
                if (!deleteRes.ok || deleteData?.ok === false) {
                  throw new Error(
                    deleteData?.detail || deleteData?.error || `Memory delete failed: ${deleteRes.status}`
                  );
                }
              } catch (e) {
                console.warn("[Profile] memory delete failed:", e);
                alert(
                  "Ava memory could not be cleared. Please try again."
                );
                return;
              }
              localStorage.removeItem("ava_user");
              sessionStorage.removeItem("ava_user");
              localStorage.removeItem("ava_memory");
              sessionStorage.removeItem("ava_memory");
              localStorage.removeItem("ava_memory_learning_enabled");
              sessionStorage.removeItem("ava_memory_learning_enabled");
              sessionStorage.setItem(
                "ava_memory_recently_cleared",
                "true"
              );
              alert("Ava memory cleared.");
              window.location.reload();
            },
            children: "Clear Memory"
          }
        ) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("section", { style: { marginTop: "20px" }, children: [
      /* @__PURE__ */ jsx("div", { style: labelStyle, children: "Ava Personality" }),
      /* @__PURE__ */ jsx("div", { style: cardStyle, children: /* @__PURE__ */ jsx("div", { style: { ...valueStyle, fontSize: "14px", opacity: 0.85 }, children: "Ava automatically adapts how she communicates based on your conversation, preferences, and context. Her tone may become more friendly, professional, direct, supportive, or neutral as needed while keeping her core personality consistent." }) })
    ] })
  ] }) });
}
export {
  Profile as default
};
